import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const spinnerVariants = cva(
  "relative inline-flex items-center justify-center",
  {
    variants: {
      size: {
        sm: "h-4 w-4",
        md: "h-6 w-6",
        lg: "h-10 w-10",
        xl: "h-14 w-14",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const ringVariants = cva(
  "absolute inset-0 rounded-full border-2 border-primary/20",
  {
    variants: {
      size: {
        sm: "border-[1.5px]",
        md: "border-2",
        lg: "border-[2.5px]",
        xl: "border-3",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const arcVariants = cva(
  "absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin",
  {
    variants: {
      size: {
        sm: "border-[1.5px] border-t-[1.5px]",
        md: "border-2 border-t-2",
        lg: "border-[2.5px] border-t-[2.5px]",
        xl: "border-3 border-t-3",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

export interface SpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size, ...props }, ref) => {
    const showInnerDot = size === "lg" || size === "xl";

    return (
      <div
        ref={ref}
        role="status"
        className={cn(spinnerVariants({ size, className }))}
        {...props}
      >
        {/* Background ring */}
        <div className={cn(ringVariants({ size }))} />
        {/* Spinning arc */}
        <div className={cn(arcVariants({ size }))} />
        {/* Inner pulsing dot for larger sizes */}
        {showInnerDot && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={cn(
                "rounded-full bg-primary/10 animate-pulse",
                size === "lg" ? "h-3 w-3" : "h-4 w-4"
              )}
            />
          </div>
        )}
        <span className="sr-only">Loading</span>
      </div>
    );
  }
);
Spinner.displayName = "Spinner";

export { Spinner, spinnerVariants };
